import React from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, TrendingDown, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import IconButton from "../../../Shared/Buttons/IconButton";
import Chip from "../../../Shared/ui/Chip";
import { color } from "../../../Shared/tokens";
import { fmt } from "../../../../Utils/formatCurrency";

// ─── allocation state helpers (shared with ProjectionsTab) ───────────────────
//
// The editing state for an allocate scenario is { qtyByProject }:
//   qtyByProject[projectId][priceBundleId] = the simulated event count for that price bundle.
// Only count/event price bundles are in scope; a measurement with no price bundle is not part of this
// simulation. An absent entry keeps the measured baseline. Changing a quantity scales that bundle's
// own measured cost and revenue proportionally (a bundle that only booked cost moves only cost).

// The editable production units on a line (its count/event price bundles).
export function unitsOf(line) {
  return Object.values(line?.units ?? {});
}

// Effective quantity for a bundle: the override if present, otherwise the measured baseline.
export function unitQty(state, projectId, unit) {
  const o = state?.qtyByProject?.[projectId]?.[unit.bundleId];
  return o != null ? Number(o) : unit.qty;
}

// Effective editing state: unsaved edits if present, otherwise derived from the saved scenario.
export function resolveAllocationState(activeScenario, editAllocation /*, lineAgg */) {
  if (editAllocation) return editAllocation;
  const qtyByProject = {};
  (activeScenario?.items || []).forEach((it) => {
    const a = it.allocationData;
    if (!a || a.projectId == null || a.priceBundleId == null) return;
    if (!qtyByProject[a.projectId]) qtyByProject[a.projectId] = {};
    qtyByProject[a.projectId][a.priceBundleId] = Number(a.newQuantity);
  });
  return { qtyByProject };
}

// Convert an allocation state to scenario items — one item per bundle whose quantity differs from its
// measured baseline. Needs lineAgg to know each bundle's baseline.
export function allocationStateToItems(state, lineAgg) {
  if (!state || !lineAgg) return [];
  const items = [];
  Object.values(lineAgg).forEach((line) => {
    unitsOf(line).forEach((u) => {
      const override = state.qtyByProject?.[line.projectId]?.[u.bundleId];
      if (override == null || Math.abs(Number(override) - u.qty) < 0.0001) return;
      items.push({
        itemId: 0,
        newValue: null,
        allocationData: { projectId: line.projectId, priceBundleId: u.bundleId, newQuantity: Number(override) },
      });
    });
  });
  return items;
}

// ─── UI ──────────────────────────────────────────────────────────────────────

const monoLabel = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  color: "var(--color-text-secondary)",
  fontFamily: "'DM Mono', monospace",
  marginBottom: 6,
};

const formatQty = (v) => Math.round(v).toLocaleString();

// A numeric input with its own display state so typing decimals/commas stays smooth.
function AmountInput({ value, disabled, onCommit }) {
  const [display, setDisplay] = React.useState(() => String(value ?? 0));
  const lastRef = React.useRef(value);
  React.useLayoutEffect(() => {
    if (Number(value) !== Number(lastRef.current)) {
      lastRef.current = value;
      setDisplay(String(value ?? 0));
    }
  }, [value]);
  const handleChange = (e) => {
    const raw = e.target.value.replace(",", ".");
    if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
    setDisplay(raw);
    if (raw !== "" && !isNaN(parseFloat(raw))) {
      const num = Math.max(0, parseFloat(raw));
      lastRef.current = num;
      onCommit(num);
    }
  };
  const handleBlur = () => {
    if (display === "" || isNaN(parseFloat(display))) { setDisplay("0"); lastRef.current = 0; onCommit(0); }
  };
  return (
    <input
      type="text"
      value={display}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      style={{
        width: "100%", textAlign: "right", border: "1px solid var(--color-border-light)",
        borderRadius: 4, padding: "6px 8px", fontSize: 14,
        fontFamily: "'DM Mono', 'Courier New', monospace", outline: "none",
      }}
    />
  );
}

// One editable price-bundle row: its name + profit impact, the produced quantity input, and a small
// chip with the bundle's price per unit (green when it is income, red when it is a cost).
// stopwatchRateNet: net (rev - cost) euros per count event from machine time on this line (Option A).
function UnitRow({ unit, qty, stopwatchRateNet = 0, currency, readOnly, onSetQty, onReset, t }) {
  const baseline = unit.qty;
  const changed = Math.abs(qty - baseline) > 0.0001;
  const factor = baseline > 0 ? (qty - baseline) / baseline : 0;
  const deltaEvents = qty - baseline;
  const profitDelta = (unit.revTotal - unit.costTotal) * factor + stopwatchRateNet * deltaEvents;
  const isIncome = unit.revTotal > unit.costTotal;
  const pricePerUnit = baseline > 0 ? (unit.revTotal + unit.costTotal) / baseline : 0;
  const mono = { fontFamily: "'DM Mono', monospace" };
  const muted = { color: "var(--color-text-secondary)" };
  const posColor = color.status.success, negColor = color.status.error;
  const profitColor = profitDelta > 0.5 ? posColor : profitDelta < -0.5 ? negColor : "var(--color-text-secondary)";

  return (
    <div style={{
      border: `1px solid ${changed ? color.selected.border : "var(--color-border-light)"}`,
      background: changed ? color.selected.bg : "#fff",
      borderRadius: 8, padding: "10px 12px", marginBottom: 8,
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{unit.name}</span>
        {changed && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, ...mono, fontWeight: 700, fontSize: 13, color: profitColor, whiteSpace: "nowrap" }}>
            {profitDelta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{profitDelta >= 0 ? "+" : ""}{fmt(profitDelta, currency, 0)}
          </span>
        )}
      </div>

      <span style={{ marginTop: 8, fontSize: 11.5, ...muted, whiteSpace: "nowrap" }}>
        {t('projectionsTab.producedQtyLabel', { qty: formatQty(baseline) })}
      </span>

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 6, marginTop: 10 }}>
        <Chip variant={isIncome ? "success" : "danger"} size="extraSmall">
          {fmt(pricePerUnit, currency, 2)} {t('projectionsTab.perUnitSuffix')}
        </Chip>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {changed && !readOnly && (
            <IconButton ariaLabel={t('projectionsTab.resetToBaseline')} variant="ghost" size="small" onClick={() => onReset(unit.bundleId)}>
              <RotateCcw size={14} />
            </IconButton>
          )}
          <div style={{ width: 96 }}>
            <AmountInput value={qty} disabled={readOnly} onCommit={(v) => onSetQty(unit.bundleId, v)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// One line card: a header, an editable quantity row per count/event price bundle, and the line's net
// projected profit impact.
function LineCard({ line, state, currency, readOnly, onSetQty, onReset, t }) {
  const units = unitsOf(line);
  const mono = { fontFamily: "'DM Mono', monospace" };
  const muted = { color: "var(--color-text-secondary)" };
  const divider = { borderTop: "1px solid var(--color-border-light)", margin: "10px 0" };

  // Implicit throughput: net (rev - cost) per count event from machine time on this line.
  const stopwatchRateNet = line.totalCountQty > 0
    ? (line.stopwatchRevTotal - line.stopwatchCostTotal) / line.totalCountQty
    : 0;
  const netDeltaEvents = units.reduce((s, u) => s + (unitQty(state, line.projectId, u) - u.qty), 0);
  const bundleProfitDelta = units.reduce((sum, u) => {
    const qty = unitQty(state, line.projectId, u);
    const factor = u.qty > 0 ? (qty - u.qty) / u.qty : 0;
    return sum + (u.revTotal - u.costTotal) * factor;
  }, 0);
  const lineProfitDelta = bundleProfitDelta + stopwatchRateNet * netDeltaEvents;
  const anyChanged = units.some((u) => Math.abs(unitQty(state, line.projectId, u) - u.qty) > 0.0001);
  const posColor = color.status.success, negColor = color.status.error;

  return (
    <div style={{
      border: "1px solid var(--color-border-light)", borderRadius: 8, padding: "12px 14px",
      flex: "1 0 300px", maxWidth: 480, background: "#fff", display: "flex", flexDirection: "column",
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {line.resourceName || line.projectName || `#${line.projectId}`}
      </div>
      {line.projectName && line.projectName !== line.resourceName && (
        <div style={{ fontSize: 12, ...muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {line.projectName}
        </div>
      )}

      <div style={{ ...monoLabel, marginTop: 10, marginBottom: 2 }}>{t('projectionsTab.productionByBundle')}</div>
      <div style={{ fontSize: 11, ...muted, marginBottom: 8 }}>{t('projectionsTab.changeQtyHint')}</div>

      <div style={{ flex: 1 }}>
        {units.length === 0 ? (
          <div style={{ fontSize: 12, ...muted, fontStyle: "italic", padding: "4px 0" }}>{t('projectionsTab.noBundlesForLine')}</div>
        ) : (
          units.map((u) => (
            <UnitRow
              key={u.bundleId}
              unit={u}
              qty={unitQty(state, line.projectId, u)}
              stopwatchRateNet={stopwatchRateNet}
              currency={currency}
              readOnly={readOnly}
              onSetQty={(bundleId, v) => onSetQty(line.projectId, bundleId, v)}
              onReset={(bundleId) => onReset(line.projectId, bundleId)}
              t={t}
            />
          ))
        )}
      </div>

      {units.length > 0 && (
        <>
          {(line.stopwatchCostTotal > 0 || line.stopwatchRevTotal > 0) && line.totalCountQty > 0 && (
            <div style={{ fontSize: 11, ...muted, fontStyle: "italic", marginTop: 4 }}>
              {t('projectionsTab.inclMachineTime', { cost: fmt((line.stopwatchCostTotal + line.stopwatchRevTotal) / line.totalCountQty, currency, 2) })}
            </div>
          )}
          <div style={divider} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", ...muted }}>{t('projectionsTab.lineProfitImpact')}</span>
            <span style={{ ...mono, fontWeight: 700, fontSize: 15, color: !anyChanged ? "var(--color-text-secondary)" : lineProfitDelta >= 0 ? posColor : negColor, whiteSpace: "nowrap" }}>
              {!anyChanged ? "—" : `${lineProfitDelta >= 0 ? "+" : ""}${fmt(lineProfitDelta, currency, 0)}`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// A floating circular scroll control, pinned to one edge of the scroller and vertically centered.
function ScrollArrow({ side, onClick, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        position: "absolute", [side]: 6, top: "50%", transform: "translateY(-50%)", zIndex: 3,
        width: 34, height: 34, borderRadius: "50%", border: "1px solid var(--color-border-light)",
        background: "#fff", boxShadow: "0 2px 10px rgba(12,14,41,0.16)", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", color: "#0C0E29",
      }}
    >
      {side === "left" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
    </button>
  );
}

// A soft white fade over one edge, hinting there is more content to scroll toward.
function EdgeFade({ side }) {
  return (
    <div style={{
      position: "absolute", top: 0, bottom: 0, [side]: 0, width: 56, pointerEvents: "none", zIndex: 2,
      background: `linear-gradient(to ${side === "left" ? "right" : "left"}, #fff 15%, rgba(255,255,255,0))`,
    }} />
  );
}

// Wraps a row of cards in a horizontal scroller. When the row overflows, edge fades and clickable
// arrows appear on the overflowing side(s) so it is clear there is more to see.
function HorizontalScroller({ children }) {
  const scrollRef = React.useRef(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(false);

  const update = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 1);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
  }, [update]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.85, 300), behavior: "smooth" });
  };

  return (
    <div style={{ position: "relative", marginBottom: 14 }}>
      {canLeft && <EdgeFade side="left" />}
      {canRight && <EdgeFade side="right" />}
      {canLeft && <ScrollArrow side="left" onClick={() => scrollBy(-1)} ariaLabel="Scroll left" />}
      {canRight && <ScrollArrow side="right" onClick={() => scrollBy(1)} ariaLabel="Scroll right" />}
      <div
        ref={scrollRef}
        // Horizontal padding keeps the first/last card's border inside the scrollable area (subpixel card
        // widths otherwise clip the right border when scrolled to the end).
        style={{ display: "flex", gap: 10, overflowX: "auto", padding: "0 2px 6px", scrollbarWidth: "thin" }}
      >
        {children}
      </div>
    </div>
  );
}

// Editor body for a move-production (allocate) scenario: a card per line, each with an editable
// quantity per count/event price bundle. Changing a quantity scales that bundle's own measured
// cost + revenue proportionally. Output not priced by a bundle is out of scope.
export default function AllocationEditorBody({ lineAgg, projectOrder, activeScenario, editAllocation, onChange, readOnly, currency }) {
  const { t } = useTranslation();
  // Cards follow the graph configuration's project order (matched on registration-point name);
  // lines not in that order go last, alphabetically.
  const lines = React.useMemo(() => {
    const rank = new Map((projectOrder || []).map((name, i) => [name, i]));
    const rankOf = (l) => rank.get(l.resourceName) ?? Number.MAX_SAFE_INTEGER;
    const nameOf = (l) => l.resourceName || l.projectName || "";
    return Object.values(lineAgg || {}).sort((a, b) => rankOf(a) - rankOf(b) || nameOf(a).localeCompare(nameOf(b)));
  }, [lineAgg, projectOrder]);

  const state = resolveAllocationState(activeScenario, editAllocation, lineAgg);

  const commit = (next) => { if (!readOnly) onChange(next); };
  const setQty = (projectId, bundleId, qty) => {
    const byProject = { ...(state.qtyByProject || {}) };
    byProject[projectId] = { ...(byProject[projectId] || {}), [bundleId]: qty };
    commit({ ...state, qtyByProject: byProject });
  };
  const resetQty = (projectId, bundleId) => {
    const byProject = { ...(state.qtyByProject || {}) };
    if (byProject[projectId]) {
      const inner = { ...byProject[projectId] };
      delete inner[bundleId];
      byProject[projectId] = inner;
    }
    commit({ ...state, qtyByProject: byProject });
  };

  if (lines.length === 0) {
    return <div style={{ padding: 16, fontSize: 13, color: "var(--color-text-secondary)" }}>{t('projectionsTab.noLinesForAllocation')}</div>;
  }

  const grandProfitDelta = lines.reduce((sum, l) => {
    const units = unitsOf(l);
    const swRateNet = l.totalCountQty > 0 ? (l.stopwatchRevTotal - l.stopwatchCostTotal) / l.totalCountQty : 0;
    const netDelta = units.reduce((s, u) => s + (unitQty(state, l.projectId, u) - u.qty), 0);
    const bundleDelta = units.reduce((s, u) => {
      const qty = unitQty(state, l.projectId, u);
      const factor = u.qty > 0 ? (qty - u.qty) / u.qty : 0;
      return s + (u.revTotal - u.costTotal) * factor;
    }, 0);
    return sum + bundleDelta + swRateNet * netDelta;
  }, 0);
  const anyChanged = lines.some((l) => unitsOf(l).some((u) => Math.abs(unitQty(state, l.projectId, u) - u.qty) > 0.0001));

  return (
    <div style={{ padding: "16px", paddingBottom: "12px" }}>
      <HorizontalScroller>
        {lines.map((line) => (
          <LineCard
            key={line.projectId}
            line={line}
            state={state}
            currency={currency}
            readOnly={readOnly}
            onSetQty={setQty}
            onReset={resetQty}
            t={t}
          />
        ))}
      </HorizontalScroller>

      {anyChanged && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          padding: "10px 14px", borderRadius: 10, background: color.selected.bg, marginBottom: 12,
        }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: color.selected.text }}>{t('projectionsTab.totalImpact')}</span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, fontWeight: 700, color: grandProfitDelta >= 0 ? color.status.success : color.status.error, whiteSpace: "nowrap" }}>
            {grandProfitDelta >= 0 ? "+" : ""}{fmt(grandProfitDelta, currency, 0)}
          </span>
        </div>
      )}

      <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
        {t('projectionsTab.allocateQtyAssumptions')}
      </div>
    </div>
  );
}
